import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  resetPassword,
  requestPasswordReset,
} from "../../../Store/Apis/ReservationApi";
import LogoBg from "../../../assets/img/Logo-bg.jpg";
import Logo from "../../../assets/img/1699451121.png";

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    email: "",
    code: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get email from navigation state if available
  React.useEffect(() => {
    if (location.state?.email) {
      setFormData((prev) => ({ ...prev, email: location.state.email }));
    }
  }, [location.state]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(""); // Clear error when user starts typing
  };

  // Validation function
  const validateForm = () => {
    if (
      !formData.email ||
      !formData.code ||
      !formData.newPassword ||
      !formData.confirmPassword
    ) {
      setError("All fields are required");
      return false;
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(formData.email)) {
      setError("Invalid email address");
      return false;
    }

    if (formData.code.length !== 6 || !/^\d+$/.test(formData.code)) {
      setError("Verification code must be 6 digits");
      return false;
    }

    if (formData.newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    setError("");
    return true;
  };

  // Handle form submission
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await resetPassword(
        formData.email,
        formData.code,
        formData.newPassword
      );

      if (response.status === 200) {
        toast.success(response.message || "Password reset successfully!");
        // Navigate back to login with success message
        navigate("/login", {
          state: {
            message:
              response.message ||
              "Password reset successfully! Please login with your new password.",
          },
        });
      } else {
        toast.error(response.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("Password reset error:", error);
      toast.error(
        "Failed to reset password. Please check your verification code and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForgotPassword = () => {
    navigate("/forgot-password");
  };

  const handleResendCode = async () => {
    if (!formData.email) {
      toast.error("Email is required to resend code");
      return;
    }

    setResendLoading(true);

    try {
      const response = await requestPasswordReset(formData.email);

      if (response.status === 200) {
        toast.success(
          response.message || "Verification code resent successfully!"
        );
      } else {
        toast.error(response.message || "Failed to resend verification code");
      }
    } catch (error) {
      console.error("Resend code error:", error);
      toast.error("Failed to resend verification code");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="main-content main-content-bg mt-0 ps">
      <div
        className="page-header min-vh-100"
        style={{ backgroundImage: `url(${LogoBg})` }}
      >
        <span className="mask bg-gradient-dark opacity-6"></span>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-4 col-md-6">
              <div className="card border-0 mb-0">
                <div className="card-header text-center pt-4 pb-2 bg-transparent">
                  <img src={Logo} className="login-logo" alt="Logo" />
                </div>
                <div className="card-body px-lg-5">
                  <h1 className="fs-5 text-muted mb-3 fw-normal">
                    Reset Password
                  </h1>
                  <p className="text-sm text-muted mb-4">
                    Enter the verification code sent to your email and create a
                    new password.
                  </p>
                  <form onSubmit={handleResetPassword}>
                    <div className="form-group mb-3">
                      <label>Email address</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        readOnly={location.state?.email} // Make readonly if coming from previous page
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label>Verification Code</label>
                      <input
                        type="text"
                        name="code"
                        className="form-control"
                        placeholder="Enter 6-digit code"
                        value={formData.code}
                        onChange={handleChange}
                        maxLength="6"
                      />
                      <div className="mt-1">
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={resendLoading}
                          className="btn btn-link text-primary-faded text-sm p-0"
                        >
                          {resendLoading ? "Sending..." : "Resend Code"}
                        </button>
                      </div>
                    </div>

                    <div className="form-group mb-3">
                      <label>New Password</label>
                      <input
                        type="password"
                        name="newPassword"
                        className="form-control"
                        placeholder="Enter new password"
                        value={formData.newPassword}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label>Confirm New Password</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        className="form-control"
                        placeholder="Confirm new password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                    </div>

                    {error && (
                      <div className="text-danger text-sm mt-2 mb-3">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary mt-2 mb-3 w-100"
                      disabled={loading}
                    >
                      {loading ? "Resetting Password..." : "Reset Password"}
                    </button>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={handleBackToForgotPassword}
                        className="btn btn-link text-primary-faded text-sm p-0"
                      >
                        Back to Forgot Password
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
